import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { AlertTriangle, ArrowRight, Server, ShieldCheck } from 'lucide-react';
import { useAuthStatus } from '../hooks/useAuthStatus';
import tyrlLogo from '../assets/tyrl-logo.svg';

// The front door of an internal TYRL tool (TYR-218). Cyril signs in to his own
// scan tool, so this is a sign-in screen, not a pitch: one action, one line of
// scope, and two facts about the sign-in that the code can be checked against.
//
// The custom domain org URL field is gone. It was dead weight: the client put
// `orgUrl` on the connect query string and the server's /connect handler never
// read it, so the OAuth always went to login.salesforce.com anyway. If a TYRL
// org ever turns on the My Domain policy that blocks login.salesforce.com,
// this button breaks and the fix belongs in the server's loginUrlFor, not in
// a field here that nothing read.
export function ConnectPage() {
  const navigate = useNavigate();
  const { status, loading } = useAuthStatus();
  const [searchParams] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  const reduceMotion = useReducedMotion();

  // The callback bounces back here with ?error=not-consultant when the signed-in
  // Salesforce org is not on the CONSULTANT_ORG_IDS allow-list, so the visitor
  // reads why instead of watching a good Salesforce login land back on this page.
  const errorParam = searchParams.get('error');
  // The refused org's name (or its ID when the name lookup failed), so the
  // message says which Salesforce session the browser signed in with. It comes
  // off a query string, so cap it and render it as a text node only.
  const refusedOrg = (searchParams.get('org') ?? '').slice(0, 80).trim();

  useEffect(() => {
    if (!status?.connected) return;
    // Route by role: a connected client-admin who landed here by accident
    // should go to /hub, not get force-marched into the consultant surface.
    // A session with no role stays on this page and signs in again. Sending
    // it to /internal would bounce straight back here off the layout guard.
    if (status.role === 'client-admin') navigate('/hub', { replace: true });
    else if (status.role === 'consultant') navigate('/internal/leads', { replace: true });
  }, [status, navigate]);

  const handleConnect = () => {
    // Guard as well as disable: a double tap that lands before React has
    // re-rendered the disabled button must not start a second OAuth round-trip.
    if (submitting) return;
    setSubmitting(true);
    // intent=consultant tells the server callback to never auto-promote
    // this OAuth to client-admin even if the SF user has been invited as
    // a hub user before. Without this, a single SF identity that lives
    // in both worlds (e.g. during testing) gets permanently routed to
    // /hub once it's activated any invite. No orgType: the server defaults
    // to the production login host, which is what a TYRL org needs.
    const qs = new URLSearchParams({ intent: 'consultant' });
    window.location.href = `/api/auth/connect?${qs.toString()}`;
  };

  return (
    <div className="relative min-h-screen bg-surface-0 text-crux-light overflow-hidden">
      {/* pillar gradient line */}
      <div
        className="fixed top-0 inset-x-0 h-[2px] z-50"
        style={{
          background:
            'linear-gradient(90deg, #3D55D6 0%, #5B7CFF 33%, #9A988F 66%, #3FA96B 100%)',
        }}
      />

      {/* faint grid background */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          backgroundImage:
            'linear-gradient(rgba(27,27,27,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(27,27,27,0.05) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />

      {/* top bar */}
      <header className="fixed top-[2px] inset-x-0 z-40 px-6 sm:px-10 py-5 flex items-start justify-between gap-4">
        <img src={tyrlLogo} alt="TYRL" className="h-14 sm:h-20 w-auto opacity-90" />
        <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-neutral-400 whitespace-nowrap pt-1">
          v2.6 · Internal
        </div>
      </header>

      {/* main */}
      <main className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 py-28">
        <motion.div
          initial={reduceMotion ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: reduceMotion ? 0 : 0.45, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-[420px] bg-surface-2 border border-border shadow-card"
        >
          {/* card header */}
          <div className="px-7 sm:px-9 pt-9 pb-7 border-b border-border">
            <div className="flex items-center gap-2.5 mb-4">
              <span aria-hidden className="h-[2px] w-4 shrink-0 bg-grow" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-neutral-400">
                TYRL internal
              </span>
            </div>
            <h1 className="text-[42px] font-bold tracking-[-0.035em] leading-none text-crux-light mb-3">
              OrgIQ
            </h1>
            <p className="text-sm text-neutral-400 leading-snug text-pretty">
              Walk in knowing everything.
            </p>
          </div>

          {errorParam ? (
            <div className="px-7 sm:px-9 pt-6">
              <div
                role="alert"
                className="flex gap-2.5 px-3.5 py-3 border border-sunset/50 bg-sunset/10 text-[13px] leading-snug text-crux-light"
              >
                <AlertTriangle size={15} className="shrink-0 mt-[1px] text-sunset" aria-hidden />
                <p className="text-pretty">
                  {errorParam === 'not-consultant' ? (
                    <>
                      <strong className="font-semibold">That org has no internal access.</strong>{' '}
                      {refusedOrg ? <>Salesforce signed you in to {refusedOrg}. </> : null}
                      Sign in with a TYRL org, or ask Cyril Duvignau for a scan link.
                    </>
                  ) : (
                    <>
                      <strong className="font-semibold">Sign-in failed.</strong> Try again.
                    </>
                  )}
                </p>
              </div>
            </div>
          ) : null}

          {/* action */}
          <div className="px-7 sm:px-9 pt-7 pb-8">
            <button
              type="button"
              onClick={handleConnect}
              disabled={submitting || loading}
              aria-busy={submitting}
              className="w-full h-12 px-4 bg-sfblue text-white font-semibold text-sm tracking-[0.01em] flex items-center justify-center gap-2.5 transition-[opacity,transform] duration-150 ease-out hover:opacity-90 active:scale-[0.99] disabled:opacity-70 disabled:cursor-not-allowed disabled:active:scale-100 motion-reduce:transition-none motion-reduce:active:scale-100"
            >
              <ArrowRight size={16} className="shrink-0" aria-hidden />
              <span>{submitting ? 'Redirecting to Salesforce…' : 'Sign in with Salesforce'}</span>
            </button>

            <p className="mt-4 text-xs leading-relaxed text-neutral-400 text-pretty">
              Access is limited to TYRL Salesforce orgs.
            </p>

            {/* Facts about this sign-in, each one checkable against the code.
                "Read-only" was dropped in TYR-218: the consultant sign-in runs
                no audit at all since TYR-216, so it described nothing here. */}
            <ul className="mt-7 pt-6 border-t border-border flex flex-col gap-2.5">
              <SignInFact icon={<ShieldCheck size={13} aria-hidden />}>
                OAuth 2.0 with PKCE
              </SignInFact>
              <SignInFact icon={<Server size={13} aria-hidden />}>
                Salesforce tokens stay on the server
              </SignInFact>
            </ul>
          </div>
        </motion.div>
      </main>
    </div>
  );
}

function SignInFact({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <li className="flex items-center gap-2 text-[11px] leading-none text-neutral-400">
      <span className="shrink-0 text-neutral-500">{icon}</span>
      <span className="whitespace-nowrap">{children}</span>
    </li>
  );
}
